// The hub agent switches: the keys of the Workers KV namespace
// HUB_AGENT_SWITCHES, which the Exchange and the hub agent's Worker both
// bind, and the operator changes with `wrangler kv key put`, with no deploy.
// Two parts read it against these keys: the Exchange, which calls a hub's
// HubAgent only while `hub:<Hub ID>` is "on" (src/services/hub-agent.ts),
// and lists those keys for the daily hub agent wake
// (src/exchange/hub-agent-wake.ts), and the HubAgent, which checks every key before each agent action and
// each model request, and writes its hub's spend key
// (hub-agent/src/hosted/switches.ts). Besides the operator's wrangler and the
// HubAgent's spend keys, one route writes it: the operator's
// setHubAgentSwitch (src/exchange/hub-agent-switch.ts), only `hub:<Hub ID>`
// and `dailyCapUsd:<Hub ID>`. It imports only effect and the contract's IDs,
// as the rest of the contract does. See Design > Operations,
// "Hub agent switches".

import { Schema } from "effect";
import { HubId } from "./ids.ts";

/** The keys of HUB_AGENT_SWITCHES. */
export const HUB_AGENT_SWITCH_KEYS = {
  /** "on": the hub agent serves this hub. Any other value, or none, and nothing calls its HubAgent. */
  hub: (hubId: string) => `hub:${hubId}`,
  /** Any value: the kill switch, which stops every agent action and model request, on every hub. */
  off: "off",
  /** Each hub's daily cap, in USD. */
  dailyCap: "dailyCapUsd",
  /** One hub's daily cap, in USD, which overrides dailyCapUsd for that hub. */
  hubDailyCap: (hubId: string) => `dailyCapUsd:${hubId}`,
  /** The daily cap of every hub's spend together, in USD. */
  fleetDailyCap: "fleetDailyCapUsd",
  /** The prefix of one UTC day's spend keys, `yyyy-mm-dd`. */
  spendOf: (day: string) => `spend:${day}:`,
  /** What one hub spent on one UTC day, in the key's metadata, written only by that hub's HubAgent. */
  spend: (day: string, hubId: string) => `spend:${day}:${hubId}`,
} as const;

/** The value of `hub:<Hub ID>` that enables a hub. */
export const HUB_AGENT_ON = "on";

/** Whether a value read from `hub:<Hub ID>` enables its hub: the KV read's decode, by both readers. */
export const isHubAgentOn = Schema.is(Schema.Literal(HUB_AGENT_ON));

/**
 * The value of a cap key, `dailyCapUsd`, `dailyCapUsd:<Hub ID>` or
 * `fleetDailyCapUsd`: a decimal string of a USD number of 0 or more. The
 * HubAgent decodes each cap with it, and the Exchange the hub's own cap that
 * setHubAgentSwitch reports. See Design > Operations, "Hub agent switches".
 */
export const DailyCapUsdFromString = Schema.FiniteFromString.check(Schema.isGreaterThanOrEqualTo(0));

/** The highest hub's own daily cap that setHubAgentSwitch writes, in USD: a guard against a typo, not a budget. */
export const MAX_HUB_DAILY_CAP_USD = 1000;

/**
 * PUT /api/v1/hubs/:hub/agent/switch's body, operator only: `on`, whether the
 * hub agent switches enable the hub, and `dailyCapUsd`, the hub's own daily
 * cap in USD, from 0 to MAX_HUB_DAILY_CAP_USD; `null` deletes the hub's own
 * cap, so `dailyCapUsd` or its default applies to it again, and leaving it
 * out leaves the cap as it is. See Design > Operations, "Hub agent switches".
 */
export const SetHubAgentSwitch = Schema.Struct({
  on: Schema.Boolean,
  dailyCapUsd: Schema.optionalKey(Schema.NullOr(Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0), Schema.isLessThanOrEqualTo(MAX_HUB_DAILY_CAP_USD)))),
}).annotate({ identifier: "SetHubAgentSwitch" });
export type SetHubAgentSwitch = typeof SetHubAgentSwitch.Type;

/**
 * PUT /api/v1/hubs/:hub/agent/switch's success, 200: the hub's switches as
 * the route wrote them, `on` and the hub's own daily cap in USD, `null` when
 * the hub has none, so `dailyCapUsd` or its default applies. KV takes up to a
 * minute to carry a write to every reader. See Design > Operations, "Hub
 * agent switches".
 */
export const HubAgentSwitchState = Schema.Struct({ hubId: HubId, on: Schema.Boolean, dailyCapUsd: Schema.NullOr(Schema.Finite) }).annotate({ identifier: "HubAgentSwitchState" });
export type HubAgentSwitchState = typeof HubAgentSwitchState.Type;
